import { useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Camera, Upload, X, Loader2, CheckCircle2, Package, ImageIcon, ChevronLeft, ChevronRight, Search, UserRound, Minus, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { CatalogIcon } from "@/lib/catalogIcons";

/* ====================== Image Uploader ====================== */
function ImageUploader({ label, value, onChange, kind = "idcard", testId }) {
  const cameraRef = useRef();
  const galleryRef = useRef();
  const [uploading, setUploading] = useState(false);
  const preview = value?.preview;
  const path = value?.path;

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const local = URL.createObjectURL(file);
    onChange({ preview: local, path: null });
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await http.post(`/uploads?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({ preview: local, path: res.data.storage_path });
    } catch {
      toast.error("Yükleme başarısız");
      onChange(null);
    } finally {
      setUploading(false);
    }
  };

  const clear = () => {
    onChange(null);
    if (cameraRef.current) cameraRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
  };

  return (
    <div data-testid={testId}>
      <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono mb-2 block">{label}</Label>
      {!preview ? (
        <div className="rounded-xl border-2 border-dashed border-slate-700 bg-slate-900/40 p-4 flex flex-col items-center justify-center gap-3 aspect-[16/10]">
          <div className="text-slate-400 text-xs">Fotoğraf kaynağını seç</div>
          <div className="grid grid-cols-2 gap-2 w-full max-w-xs">
            <button type="button" onClick={() => cameraRef.current?.click()} data-testid={`${testId}-camera`}
              className="flex flex-col items-center gap-1 py-3 rounded-xl bg-blue-600/15 border border-blue-500/30 text-blue-200 hover:bg-blue-600/25 transition">
              <Camera size={22} />
              <span className="text-xs font-semibold">Kamera</span>
            </button>
            <button type="button" onClick={() => galleryRef.current?.click()} data-testid={`${testId}-gallery`}
              className="flex flex-col items-center gap-1 py-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 hover:bg-amber-500/20 transition">
              <ImageIcon size={22} />
              <span className="text-xs font-semibold">Galeri / Dosya</span>
            </button>
          </div>
        </div>
      ) : (
        <div className="relative aspect-[16/10] rounded-xl overflow-hidden border border-slate-700">
          <img src={preview} alt={label} className="absolute inset-0 w-full h-full object-cover" />
          {uploading && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <Loader2 className="animate-spin text-blue-300" />
            </div>
          )}
          {!uploading && path && (
            <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/90 text-white text-[10px] font-semibold">
              <CheckCircle2 size={12} /> yüklendi
            </div>
          )}
          <button type="button" onClick={clear} data-testid={`${testId}-clear`}
            className="absolute top-2 right-2 h-7 w-7 rounded-full bg-slate-900/80 border border-slate-700 flex items-center justify-center hover:bg-red-500/80">
            <X size={14} />
          </button>
        </div>
      )}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden"
             onChange={handleChange} data-testid={`${testId}-camera-input`} />
      <input ref={galleryRef} type="file" accept="image/*" className="hidden"
             onChange={handleChange} data-testid={`${testId}-gallery-input`} />
    </div>
  );
}

/* ====================== Catalog Browser (iOS-style) ====================== */
function CatalogBrowser({ catalogs, products, onPick, query, setQuery }) {
  // path: array of catalog ids. [] = kök
  const [path, setPath] = useState([]);

  const currentParentId = path.length ? path[path.length - 1] : null;
  const catalogIds = new Set(catalogs.map((c) => c.id));
  const parentOf = (c) => (c.parent_id && catalogIds.has(c.parent_id) ? c.parent_id : null);
  const subFolders = catalogs.filter((c) => parentOf(c) === currentParentId)
                             .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.name.localeCompare(b.name));
  const localProducts = products.filter((p) => (catalogIds.has(p.catalog_id) ? p.catalog_id : null) === currentParentId);

  const breadcrumbs = useMemo(() => {
    const map = Object.fromEntries(catalogs.map((c) => [c.id, c]));
    return path.map((id) => map[id]).filter(Boolean);
  }, [path, catalogs]);

  // SEARCH: tüm ürünlerde arama
  const trimmed = (query || "").trim().toLowerCase();
  const searchResults = trimmed
    ? products.filter((p) =>
        p.name.toLowerCase().includes(trimmed) ||
        p.sku.toLowerCase().includes(trimmed) ||
        (p.category || "").toLowerCase().includes(trimmed))
    : null;

  const dirKey = path.join("/") || "root";

  return (
    <div className="relative">
      {/* Üst bar: breadcrumb + arama */}
      <div className="flex flex-col gap-2 mb-3">
        <div className="flex items-center gap-1 text-sm overflow-x-auto scrollbar-none">
          <button
            type="button"
            onClick={() => setPath([])}
            className={`px-2 py-1 rounded-md transition ${path.length === 0 ? "bg-blue-500/20 text-blue-200" : "text-slate-400 hover:text-white"}`}
            data-testid="catalog-breadcrumb-root"
          >
            Kataloglar
          </button>
          {breadcrumbs.map((b, i) => (
            <div key={b.id} className="flex items-center gap-1 shrink-0">
              <ChevronRight size={14} className="text-slate-600" />
              <button
                type="button"
                onClick={() => setPath(path.slice(0, i + 1))}
                className={`px-2 py-1 rounded-md transition ${i === breadcrumbs.length - 1 ? "bg-blue-500/20 text-blue-200" : "text-slate-400 hover:text-white"}`}
                data-testid={`catalog-breadcrumb-${b.id}`}
              >
                {b.name}
              </button>
            </div>
          ))}
          {path.length > 0 && (
            <button
              type="button"
              onClick={() => setPath(path.slice(0, -1))}
              className="ml-auto flex items-center gap-1 text-xs text-slate-400 hover:text-white px-2 py-1 rounded-md hover:bg-slate-800 shrink-0"
              data-testid="catalog-back"
            >
              <ChevronLeft size={14} /> Geri
            </button>
          )}
        </div>

        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            placeholder="Tüm ürünlerde ara..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="bg-slate-950/50 border-slate-700 pl-9"
            data-testid="product-search"
          />
        </div>
      </div>

      {/* Arama modu */}
      {searchResults ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[60vh] overflow-y-auto pr-1">
          {searchResults.map((p) => (
            <ProductCard key={p.id} p={p} onPick={onPick} />
          ))}
          {searchResults.length === 0 && (
            <div className="col-span-full text-center text-slate-500 py-10 text-sm">Ürün bulunamadı</div>
          )}
        </div>
      ) : (
        /* iOS-style zoom + fade transition between folders */
        <div className="relative overflow-hidden" style={{ minHeight: 220 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={dirKey}
              initial={{ opacity: 0, scale: 0.92, filter: "blur(6px)" }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
              exit={{ opacity: 0, scale: 1.06, filter: "blur(6px)" }}
              transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
              className="grid grid-cols-3 sm:grid-cols-4 gap-3"
            >
              {subFolders.map((f) => {
                const childCount = catalogs.filter((c) => c.parent_id === f.id).length;
                const prodCount = products.filter((p) => p.catalog_id === f.id).length;
                return (
                  <motion.button
                    key={f.id}
                    type="button"
                    onClick={() => setPath([...path, f.id])}
                    whileTap={{ scale: 0.92 }}
                    whileHover={{ y: -2 }}
                    data-testid={`folder-${f.id}`}
                    className="group relative flex flex-col items-center gap-2 p-3 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-slate-700 transition"
                  >
                    <div
                      className="h-16 w-16 rounded-2xl flex items-center justify-center shadow-lg relative"
                      style={{
                        background: `linear-gradient(135deg, ${f.color}44, ${f.color}11)`,
                        border: `1px solid ${f.color}55`,
                      }}
                    >
                      <CatalogIcon name={f.icon} size={28} className="drop-shadow" />
                      <div className="absolute inset-0 rounded-2xl ring-1 ring-white/5 pointer-events-none" />
                    </div>
                    <div className="text-xs font-semibold text-center w-full break-words hyphens-auto leading-tight" lang="tr" title={f.name}>
                      {f.name}
                    </div>
                    <div className="text-[10px] font-mono text-slate-500">
                      {childCount > 0 && `${childCount} klasör`}
                      {childCount > 0 && prodCount > 0 && " · "}
                      {prodCount > 0 && `${prodCount} ürün`}
                      {childCount === 0 && prodCount === 0 && "boş"}
                    </div>
                  </motion.button>
                );
              })}

              {localProducts.map((p) => (
                <ProductCard key={p.id} p={p} onPick={onPick} />
              ))}

              {subFolders.length === 0 && localProducts.length === 0 && (
                <div className="col-span-full text-center text-slate-500 py-10 text-sm">
                  Bu klasör boş. Koordinatör olarak ürün ekleyebilirsiniz.
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

function ProductCard({ p, onPick }) {
  return (
    <motion.button
      type="button"
      onClick={() => onPick(p)}
      whileTap={{ scale: 0.95 }}
      whileHover={{ y: -2 }}
      data-testid={`product-opt-${p.sku}`}
      className="relative flex flex-col items-center gap-2 p-3 rounded-2xl bg-gradient-to-br from-blue-600/15 to-slate-900/60 border border-blue-500/20 hover:border-blue-400/50 transition"
    >
      <div className="h-16 w-16 rounded-2xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
        <Package size={28} className="text-blue-300" />
      </div>
      <div className="text-xs font-semibold text-center w-full break-words hyphens-auto leading-tight min-h-[2em]" lang="tr" title={p.name}>
        {p.name}
      </div>
      <div className="text-[10px] font-mono text-amber-300">{p.points}p</div>
    </motion.button>
  );
}

/* ====================== Wizard ====================== */
const STEPS = [
  { key: "product", title: "Ürün Seçimi", icon: Package },
  { key: "cart", title: "Sepet", icon: ShoppingCart },
  { key: "customer", title: "Müşteri Bilgisi", icon: UserRound },
  { key: "idcard", title: "Kimlik Fotoğrafları", icon: Upload },
];

const stepVariants = {
  enter: (dir) => ({ opacity: 0, scale: dir > 0 ? 0.86 : 1.1, filter: "blur(6px)" }),
  center: { opacity: 1, scale: 1, filter: "blur(0px)" },
  exit: (dir) => ({ opacity: 0, scale: dir > 0 ? 1.1 : 0.86, filter: "blur(6px)" }),
};

const validName = (n) => {
  const parts = n.trim().split(/\s+/);
  return parts.length >= 2 && parts.every((p) => p.length >= 2);
};

function Stepper({ value, onChange, size = "lg", testPrefix }) {
  const big = size === "lg";
  const btn = big ? "h-14 w-14 rounded-2xl" : "h-9 w-9 rounded-xl";
  return (
    <div className="flex items-center justify-center gap-3">
      <motion.button type="button" whileTap={{ scale: 0.88 }} onClick={() => onChange(Math.max(1, value - 1))}
        className={`${btn} bg-slate-800 border border-slate-700 flex items-center justify-center`} data-testid={`${testPrefix}-minus`}>
        <Minus size={big ? 24 : 16} />
      </motion.button>
      <span className={`${big ? "text-4xl w-20" : "text-lg w-8"} text-center font-display font-bold`} data-testid={`${testPrefix}-value`}>{value}</span>
      <motion.button type="button" whileTap={{ scale: 0.88 }} onClick={() => onChange(value + 1)}
        className={`${btn} bg-blue-600 flex items-center justify-center`} data-testid={`${testPrefix}-plus`}>
        <Plus size={big ? 24 : 16} />
      </motion.button>
    </div>
  );
}

function QtySheet({ product, onClose, onAdd }) {
  const [q, setQ] = useState(1);
  return (
    <AnimatePresence>
      {product && (
        <motion.div className="absolute inset-0 z-20 bg-black/60 backdrop-blur-sm flex items-center justify-center p-6"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
                      transition={{ duration: 0.25, ease: [0.32, 0.72, 0, 1] }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full max-w-sm rounded-3xl bg-slate-900 border border-slate-700 p-6 space-y-6" data-testid="qty-sheet">
            <div className="text-center">
              <div className="mx-auto h-14 w-14 rounded-2xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center mb-3">
                <Package size={26} className="text-blue-300" />
              </div>
              <div className="font-display font-bold text-lg">{product.name}</div>
              <div className="text-xs font-mono text-slate-500">{product.sku} · {product.points}p / adet</div>
            </div>
            <Stepper value={q} onChange={setQ} testPrefix="qty" />
            <div className="text-center text-amber-300 font-display text-2xl font-bold" data-testid="qty-sheet-points">{product.points * q} puan</div>
            <div className="grid grid-cols-2 gap-2">
              <Button type="button" variant="outline" onClick={onClose} className="border-slate-700" data-testid="qty-sheet-cancel">Vazgeç</Button>
              <Button type="button" onClick={() => onAdd(product, q)} className="bg-blue-600 hover:bg-blue-500" data-testid="qty-sheet-add">
                <ShoppingCart size={16} className="mr-1" /> Sepete Ekle
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CartStep({ cart, setQty, remove, onAddMore }) {
  const total = cart.reduce((a, c) => a + c.product.points * c.qty, 0);
  return (
    <div className="space-y-3">
      <AnimatePresence initial={false}>
        {cart.map((c) => (
          <motion.div key={c.product.id} layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }} className="flex items-center gap-3 p-3 rounded-2xl bg-slate-900/70 border border-slate-800"
                      data-testid={`cart-item-${c.product.sku}`}>
            <div className="h-11 w-11 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center shrink-0">
              <Package size={20} className="text-blue-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm break-words leading-tight">{c.product.name}</div>
              <div className="text-[11px] font-mono text-amber-300">{c.product.points * c.qty}p</div>
            </div>
            <Stepper value={c.qty} onChange={(v) => setQty(c.product.id, v)} size="sm" testPrefix={`cart-qty-${c.product.sku}`} />
            <button type="button" onClick={() => remove(c.product.id)} className="h-9 w-9 rounded-xl flex items-center justify-center text-red-400 hover:bg-red-500/10"
                    data-testid={`cart-remove-${c.product.sku}`}>
              <Trash2 size={16} />
            </button>
          </motion.div>
        ))}
      </AnimatePresence>
      {cart.length === 0 && <div className="text-center text-slate-500 py-10 text-sm">Sepet boş</div>}
      <button type="button" onClick={onAddMore} data-testid="cart-add-more"
              className="w-full py-4 rounded-2xl border-2 border-dashed border-blue-500/40 text-blue-300 font-semibold flex items-center justify-center gap-2 hover:bg-blue-500/10 transition">
        <Plus size={18} /> Ürün Ekle
      </button>
      <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 px-5 py-4 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-amber-200 font-mono">Toplam Puan</span>
        <span className="font-display text-3xl font-bold text-amber-300" data-testid="cart-total-points">{total}</span>
      </div>
    </div>
  );
}

function CustomerStep({ name, setName, note, setNote, touched }) {
  const invalid = touched && !validName(name);
  return (
    <div className="space-y-5">
      <div>
        <Label className="text-slate-300">Müşteri Ad Soyad <span className="text-red-400">*</span></Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus
               placeholder="Örn: Ahmet Yılmaz"
               className={`mt-1.5 h-14 text-lg bg-slate-950/50 ${invalid ? "border-red-500" : "border-slate-700"}`}
               data-testid="sale-customer-name" />
        {invalid && <p className="text-xs text-red-400 mt-1.5" data-testid="sale-customer-name-error">Ad ve soyad zorunludur (en az iki kelime)</p>}
      </div>
      <div>
        <Label className="text-slate-300">İşlem Notu (opsiyonel)</Label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Örn: Müşteri 3 yıllık taahhüt ile aldı"
                  className="mt-1.5 bg-slate-950/50 border-slate-700" data-testid="sale-note" rows={4} />
      </div>
    </div>
  );
}

function WizardHeader({ step, onBack, onClose }) {
  const S = STEPS[step];
  return (
    <div className="shrink-0 px-4 pt-4 pb-3 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-xl">
      <div className="flex items-center gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onBack} disabled={step === 0}
                className={step === 0 ? "invisible" : ""} data-testid="wizard-back">
          <ChevronLeft size={18} /> Geri
        </Button>
        <div className="flex-1 text-center">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Adım {step + 1} / {STEPS.length}</div>
          <div className="font-display font-bold text-lg flex items-center justify-center gap-2" data-testid="wizard-step-title">
            <S.icon size={16} className="text-blue-400" /> {S.title}
          </div>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onClose} data-testid="wizard-close">
          <X size={18} />
        </Button>
      </div>
      <div className="flex gap-1.5 mt-3">
        {STEPS.map((s, i) => (
          <div key={s.key} className="h-1 flex-1 rounded-full bg-slate-800 overflow-hidden">
            <motion.div className="h-full bg-blue-500" initial={false}
                        animate={{ width: i <= step ? "100%" : "0%" }} transition={{ duration: 0.3 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function WizardFooter({ step, cartCount, onNext, onSubmit, saving, disabled }) {
  if (step === 0 && cartCount === 0) return null;
  return (
    <div className="shrink-0 p-4 border-t border-slate-800/80 bg-slate-950/90 backdrop-blur-xl">
      {step === 0 ? (
        <Button type="button" onClick={onNext} className="w-full py-6 bg-blue-600 hover:bg-blue-500 text-base" data-testid="wizard-go-cart">
          <ShoppingCart size={18} className="mr-2" /> Sepete Git ({cartCount})
        </Button>
      ) : step < 3 ? (
        <Button type="button" onClick={onNext} disabled={step === 1 && cartCount === 0}
                className="w-full py-6 bg-blue-600 hover:bg-blue-500 text-base" data-testid="wizard-next">
          Devam <ChevronRight size={18} className="ml-1" />
        </Button>
      ) : (
        <Button type="button" onClick={onSubmit} disabled={saving || disabled}
                className="w-full py-6 bg-emerald-600 hover:bg-emerald-500 text-base shadow-[0_8px_32px_rgba(16,185,129,0.3)]"
                data-testid="sale-submit">
          {saving ? <Loader2 className="animate-spin" /> : `Satışı Kaydet (${cartCount} ürün)`}
        </Button>
      )}
    </div>
  );
}

/* ====================== Main Page ====================== */
export default function NewSale() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [query, setQuery] = useState("");
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [cart, setCart] = useState([]);
  const [picking, setPicking] = useState(null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [touched, setTouched] = useState(false);
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [saving, setSaving] = useState(false);
  const isMobile = window.innerWidth < 1024;

  useEffect(() => {
    Promise.all([http.get("/products?active_only=true"), http.get("/catalogs")])
      .then(([pr, cr]) => { setProducts(pr.data); setCatalogs(cr.data); });
  }, []);

  const go = (to) => { setDir(to > step ? 1 : -1); setStep(to); };

  const addToCart = (product, qty) => {
    setCart((c) => {
      const ex = c.find((x) => x.product.id === product.id);
      return ex ? c.map((x) => (x.product.id === product.id ? { ...x, qty: x.qty + qty } : x)) : [...c, { product, qty }];
    });
    setPicking(null);
    setQuery("");
    go(1);
  };
  const setQty = (id, qty) => setCart((c) => c.map((x) => (x.product.id === id ? { ...x, qty } : x)));
  const removeItem = (id) => setCart((c) => c.filter((x) => x.product.id !== id));

  const next = () => {
    if (step === 2) {
      setTouched(true);
      if (!validName(name)) return;
    }
    go(step + 1);
  };

  const uploading = (front && !front.path) || (back && !back.path);

  const submit = async () => {
    if (cart.length === 0 || !validName(name)) return;
    setSaving(true);
    try {
      const { data } = await http.post("/sales/basket", {
        items: cart.map((c) => ({ product_id: c.product.id, quantity: c.qty })),
        customer_name: name.trim(),
        customer_note: note || null,
        id_card_front_path: front?.path || null,
        id_card_back_path: back?.path || null,
      });
      toast.success(`Satış kaydedildi · ${data.basket_code} · ${data.total_points} puan`);
      nav("/sales");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  const wizard = (
    <div className={isMobile ? "fixed inset-0 z-[60] bg-slate-950 flex flex-col"
                             : "relative flex flex-col max-w-3xl mx-auto min-h-[calc(100vh-8rem)] rounded-3xl border border-slate-800 overflow-hidden bg-slate-950"}
         data-testid="new-sale-wizard">
      <WizardHeader step={step} onBack={() => go(step - 1)} onClose={() => nav("/sales")} />

      <div className="relative flex-1 overflow-y-auto overflow-x-hidden">
        <AnimatePresence mode="wait" custom={dir} initial={false}>
          <motion.div key={step} custom={dir} variants={stepVariants}
                      initial="enter" animate="center" exit="exit"
                      transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                      className="p-4 sm:p-6" data-testid={`wizard-step-${STEPS[step].key}`}>
            {step === 0 && (
              <CatalogBrowser catalogs={catalogs} products={products}
                              query={query} setQuery={setQuery} onPick={setPicking} />
            )}
            {step === 1 && <CartStep cart={cart} setQty={setQty} remove={removeItem} onAddMore={() => go(0)} />}
            {step === 2 && <CustomerStep name={name} setName={setName} note={note} setNote={setNote} touched={touched} />}
            {step === 3 && (
              <div className="space-y-4">
                <p className="text-xs text-slate-500">Ön ve arka yüz fotoğraflarını yükleyin.</p>
                <ImageUploader label="Kimlik Ön Yüz" value={front} onChange={setFront} testId="idcard-front" />
                <ImageUploader label="Kimlik Arka Yüz" value={back} onChange={setBack} testId="idcard-back" />
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <WizardFooter step={step} cartCount={cart.length} onNext={step === 0 ? () => go(1) : next}
                    onSubmit={submit} saving={saving} disabled={uploading} />
      <QtySheet key={picking?.id} product={picking} onClose={() => setPicking(null)} onAdd={addToCart} />
    </div>
  );
  return isMobile ? createPortal(wizard, document.body) : wizard;
}
